import type { SyncStatus, TimeOffEntry } from '../../../api/timeOff';
import { shouldShowTimeOffEntryInGrid } from './timeOffEntriesGridFilters';
import { getTimeOffEntrySyncState } from './timeOffEntryMutability';

export type TimeOffSyncNoticeTone = 'warning' | 'danger';

export interface TimeOffSyncNotice {
  tone: TimeOffSyncNoticeTone;
  message: string;
}

function actionableTimeOffEntries(entries: TimeOffEntry[], now = new Date()): TimeOffEntry[] {
  return entries.filter((entry) =>
    shouldShowTimeOffEntryInGrid(entry, { showCancelled: false, showPast: false, now })
  );
}

export function buildTimeOffSyncNotice(
  syncStatus: SyncStatus | null,
  entries: TimeOffEntry[] = [],
  now = new Date()
): TimeOffSyncNotice | null {
  const actionableEntries = actionableTimeOffEntries(entries, now);

  const lastErrors = syncStatus?.lastErrors ?? 0;
  if (lastErrors > 0) {
    return {
      tone: 'danger',
      message: `Last calendar sync reported ${lastErrors} error${lastErrors === 1 ? '' : 's'}. Try Sync now or check sync jobs.`
    };
  }

  const dirtyLinks = syncStatus?.dirtyLinks ?? 0;
  if (dirtyLinks > 0) {
    return {
      tone: 'warning',
      message: `${dirtyLinks} calendar update${dirtyLinks === 1 ? '' : 's'} waiting to sync with Outlook.`
    };
  }

  const dirtyEntryCount = actionableEntries.filter((entry) => getTimeOffEntrySyncState(entry) === 'Waiting').length;
  if (dirtyEntryCount > 0) {
    return {
      tone: 'warning',
      message: `${dirtyEntryCount} ${dirtyEntryCount === 1 ? 'entry has' : 'entries have'} changes waiting to sync with Outlook.`
    };
  }

  const errorEntryCount = actionableEntries.filter((entry) => getTimeOffEntrySyncState(entry) === 'Error').length;
  if (errorEntryCount > 0) {
    return {
      tone: 'danger',
      message: `${errorEntryCount} ${errorEntryCount === 1 ? 'entry has' : 'entries have'} calendar sync errors.`
    };
  }

  const identityCount = actionableEntries.filter((entry) => entry.needsEmployeeIdentity).length;
  if (identityCount > 0) {
    return {
      tone: 'warning',
      message: `${identityCount} ${identityCount === 1 ? 'entry needs' : 'entries need'} an employee assigned.`
    };
  }

  const noTargetCount = actionableEntries.filter((entry) => getTimeOffEntrySyncState(entry) === 'No targets').length;
  if (noTargetCount > 0) {
    return {
      tone: 'warning',
      message: `${noTargetCount} ${noTargetCount === 1 ? 'entry has' : 'entries have'} no calendar target.`
    };
  }

  return null;
}
